import {Box, Button, Grid, Paper, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import moment from 'moment-timezone';
import React, {useEffect, useState} from 'react';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import {i18n} from 'src/i18n';
import {calcTareasCount} from 'src/modules/planificadorTarea/calcTareasCount';
import planificadorTareaEnumerators from 'src/modules/planificadorTarea/planificadorTareaEnumerators';
import {
  customSequenceOptions as buildCustomSequenceOptions,
  resolveCustomSequenceValue,
} from 'src/modules/settings/customSequenceModel';
import ModeloTareaAutocompleteFormItem from 'src/view/modeloTarea/autocomplete/ModeloTareaAutocompleteFormItem';
import ModeloTareaListItem from 'src/view/modeloTarea/list/ModeloTareaListItem';
import PuestoAutocompleteFormItem from 'src/view/puesto/autocomplete/PuestoAutocompleteFormItem';
import DatePickerFormItem from 'src/view/shared/form/items/DatePickerFormItem';
import InputFormItem from 'src/view/shared/form/items/InputFormItem';
import InputNumberFormItem from 'src/view/shared/form/items/InputNumberFormItem';
import RadioFormItem from 'src/view/shared/form/items/RadioFormItem';
import SelectFormItem from 'src/view/shared/form/items/SelectFormItem';
import SwitchFormItem from 'src/view/shared/form/items/SwitchFormItem';
import ConfirmModal from 'src/view/shared/modals/ConfirmModal';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';

import {Add, InfoOutlined, MoreVert, Redo} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {IconButton, Tooltip, TooltipProps, styled, tooltipClasses} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import modeloTareaEnumerators from 'src/modules/modeloTarea/modeloTareaEnumerators';
import AsignadorFuncionAutocompleteFormItem from 'src/view/asignadorFuncion/autocomplete/AsignadorFuncionAutocompleteFormItem';
import DayOfWeekPicker from 'src/view/shared/form/items/DayOfWeekPicker';
import Message from 'src/view/shared/message';
import BasicItemMenu from './BasicItemMenu';
import PlanificacionResumenPanel from './PlanificacionResumenPanel';

import {useSelector} from 'react-redux';
import settingsSelectors from 'src/modules/settings/settingsSelectors';

const HtmlTooltip = styled(({className, ...props}: TooltipProps) => <Tooltip {...props} classes={{popper: className}} />)(
  ({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }),
);

export default function PlanificadorMultiTareas(props) {
  const {name, disabled, fromPlanificacion, showResumen = false, onCloseResumen} = props;
  const [confirm, setConfirm] = useState(false);
  const [expanded, setExpanded] = useState(-1);

  const {control, getValues, watch, register, setValue} = useFormContext();

  const {fields, remove, append, insert} = useFieldArray({
    control,
    name,
  });
  // nuevo
  const settings = useSelector(settingsSelectors.selectSettings);

  const sequenceOptions = buildCustomSequenceOptions(settings?.customSequences || []);
  //
  const asignarHoraInicio = useWatch({
    control,
    name: `asignarHoraInicio`,
  });
  const tiempoEstimadoDefault = useWatch({
    control,
    name: `tiempoEstimadoDefault`,
  });
  // Re-render cuando cambie la programación para actualizar el contador de tareas por ejecutor
  useWatch({control, name});
  //console.log('%c⧭', 'color: #ffa640', {tiempoEstimadoDefault});

  useEffect(() => {
    fields.forEach((item: any, index) => {
      register(`${name}[${index}].tarea`);
      setValue(`${name}[${index}].tarea`, item.tarea);

      register(`${name}[${index}].prioridad`);
      setValue(`${name}[${index}].prioridad`, item.prioridad || item.tarea?.prioridad);

      register(`${name}[${index}].subtitulo`);
      setValue(`${name}[${index}].subtitulo`, item.subtitulo);

      register(`${name}[${index}].dirigidoA`);
      setValue(`${name}[${index}].dirigidoA`, item.dirigidoA);

      register(`${name}[${index}].ejecutores`);
      setValue(`${name}[${index}].ejecutores`, item.ejecutores);

      register(`${name}[${index}].teams`);
      setValue(`${name}[${index}].teams`, item.teams);

      register(`${name}[${index}].puestos`);
      setValue(`${name}[${index}].puestos`, item.puestos);

      register(`${name}[${index}].sucursales`);
      setValue(`${name}[${index}].sucursales`, item.sucursales);

      register(`${name}[${index}].activarTiempoLimite`);
      setValue(`${name}[${index}].activarTiempoLimite`, true);

      register(`${name}[${index}].tipoTiempoLimite`);
      setValue(`${name}[${index}].tipoTiempoLimite`, item.tipoTiempoLimite);

      register(`${name}[${index}].tiempoLimite`);
      setValue(`${name}[${index}].tiempoLimite`, item.tiempoLimite);

      register(`${name}[${index}].unexpirable`);
      setValue(`${name}[${index}].unexpirable`, item.unexpirable);

      register(`${name}[${index}].tiempoLimiteDiasHabiles`);
      setValue(`${name}[${index}].tiempoLimiteDiasHabiles`, item.tiempoLimiteDiasHabiles);

      register(`${name}[${index}].horaInicio`);
      setValue(`${name}[${index}].horaInicio`, item.horaInicio);

      register(`${name}[${index}].tiempoEstimado`);
      setValue(`${name}[${index}].tiempoEstimado`, item.tiempoEstimado);
      register(`${name}[${index}].horaFin`);
      setValue(`${name}[${index}].horaFin`, item.horaFin);

      register(`${name}[${index}].programar`);
      setValue(`${name}[${index}].programar`, item.programar);

      register(`${name}[${index}].fechaEjecucion`);
      setValue(`${name}[${index}].fechaEjecucion`, item.fechaEjecucion);

      register(`${name}[${index}].inicioProgramacion`);
      setValue(`${name}[${index}].inicioProgramacion`, item.inicioProgramacion);

      register(`${name}[${index}].finProgramacion`);
      setValue(`${name}[${index}].finProgramacion`, item.finProgramacion);

      register(`${name}[${index}].tipoPeriodicidad`);
      setValue(`${name}[${index}].tipoPeriodicidad`, item.tipoPeriodicidad); // || 'weeks'); // no setear hasta que se seleccione "programar"

      register(`${name}[${index}].periodicidad`);
      setValue(`${name}[${index}].periodicidad`, item.periodicidad); // || 1); // no setear hasta que se seleccione "programar"
      //
      register(`${name}[${index}].customSequenceId`);
      setValue(
        `${name}[${index}].customSequenceId`,
        resolveCustomSequenceValue(
          settings?.customSequences || [],
          item.customSequenceId || item.tarea?.customSequenceId,
          item.customSequencePrefix || item.tarea?.customSequencePrefix,
        ),
      );

      //
      register(`${name}[${index}].cantidadPorEjecucion`);
      // Si no viene valor, por defecto es 1 (lo normal es 1 tarea)
      setValue(`${name}[${index}].cantidadPorEjecucion`, item.cantidadPorEjecucion || 1);
      //

      register(`${name}[${index}].esVisada`);
      setValue(`${name}[${index}].esVisada`, item.esVisada);

      register(`${name}[${index}].aceptadores`);
      setValue(`${name}[${index}].aceptadores`, item.aceptadores);

      register(`${name}[${index}].validadores`);
      setValue(`${name}[${index}].validadores`, item.validadores);

      register(`${name}[${index}].asignarFunciones`);
      setValue(`${name}[${index}].asignarFunciones`, item.asignarFunciones);

      register(`${name}[${index}].asignadorFunciones`);
      setValue(`${name}[${index}].asignadorFunciones`, item.asignadorFunciones);
    });
  }, []);
  const [confirmarPegarIndex, setConfirmarPegarIndex] = useState<any>(null);
  const [confirmarQuitarIndex, setConfirmarQuitarIndex] = useState<any>(null);

  const doConfirm = () => {
    setConfirm(true);
  };

  const doCloseConfirm = () => {
    setConfirm(false);
  };

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const calcTiempoEstimado = (horaInicio, horaFin) => {
    return moment(horaFin).diff(moment(horaInicio), 'minutes', true);
  };
  const calcHoraFin = (horaInicio, tiempoEstimado) => {
    if (!horaInicio || !isPositiveOrZero(tiempoEstimado)) return null;
    return moment(horaInicio).add(tiempoEstimado, 'minutes');
  };

  // la fecha (y hora) mínima de inicio es la del fin máximo de sus predecesores
  const calcMinDate = (item, defaultDate: any = null) => {
    const predecesores = item.tarea.predecessors;
    if (!predecesores?.length) return defaultDate;
    const maxFin = predecesores.reduce((max, predecessorId) => {
      const predecessorIndex = fields.findIndex((item: any) => item.tarea.id === predecessorId);
      const predecessorFin = getEndOfTask(predecessorIndex);
      if (!predecessorFin) return max;
      if (!max || predecessorFin.isAfter(max)) return predecessorFin;
      return max;
    }, defaultDate);
    return maxFin;
  };

  // calcular inicio de programacion considerando días de la semana seleccionados
  const calcInicioProgramacion = (index) => {
    const programar = getValues(`${name}[${index}].programar`);
    const inicioProgramacion = getValues(`${name}[${index}].inicioProgramacion`);
    if (!programar || !inicioProgramacion) return null;
    const tipoPeriodicidad = getValues(`${name}[${index}].tipoPeriodicidad`);
    const diasSemana = getValues(`${name}[${index}].daysOfWeek`);
    const inicioProgramacionResult = moment(inicioProgramacion);
    // considerar días de semana si la es programacion semanal
    if (tipoPeriodicidad === 'weeks' && diasSemana?.length) {
      // buscar dia de la semana mayor o igual al del inicio de la programacion
      const diaSemanaProgramacion = inicioProgramacionResult.isoWeekday(); // domingo = 7
      let dayToAdd = diasSemana.find((dia) => dia >= diaSemanaProgramacion);
      // si los días de semana seleccionados son menores al del inicio de la programacion se agrega una semana
      if (dayToAdd) {
        inicioProgramacionResult.startOf('week').add(dayToAdd - 1, 'days');
      } else {
        inicioProgramacionResult
          .add(1, 'weeks')
          .startOf('week')
          .add(diasSemana[0] - 1, 'days');
      }
    }
    setValue(`${name}[${index}].inicioProgramacion`, inicioProgramacionResult.toDate());
    calcFinProgramacion(index, inicioProgramacionResult);
    setHoraInicio(index, fields[index], combineDateAndTime(inicioProgramacionResult, getHoraInicio(index)));
    return inicioProgramacionResult;
  };

  const calcFinProgramacion = (index, inicioProgramacion) => {
    // si la fecha de fin de la programacion es menor a la fecha de inicio de la programacion se ajusta la fecha de fin
    const finProgramacion = getValues(`${name}[${index}].finProgramacion`);
    if (finProgramacion && moment(finProgramacion).isBefore(inicioProgramacion)) {
      setValue(`${name}[${index}].finProgramacion`, inicioProgramacion.toDate());
    }
  };

  const getHoraInicio = (index) => {
    const horaInicio = getValues(`${name}[${index}].horaInicio`);
    return horaInicio;
  };
  const getHoraFin = (index) => {
    const horaFin = getValues(`${name}[${index}].horaFin`);
    return horaFin;
  };

  const getInicioProgramacion = (index) => {
    const inicioProgramacion = getValues(`${name}[${index}].inicioProgramacion`);
    return inicioProgramacion;
  };

  const getFechaEjecucion = (index) => {
    const fechaEjecucion = getInicioProgramacion(index) || getValues(`${name}[${index}].fechaEjecucion`);
    return fechaEjecucion ? moment(fechaEjecucion).startOf('day') : null;
  };

  const getFechaLimite = (index) => {
    const fechaEjecucion = getFechaEjecucion(index);
    if (fechaEjecucion) {
      const tiempoLimite = getValues(`${name}[${index}].tiempoLimite`);
      const tipoTiempoLimite = getValues(`${name}[${index}].tipoTiempoLimite`);
      return fechaEjecucion.add(tiempoLimite, tipoTiempoLimite).endOf('day');
    }
    return null;
  };

  // de una cantidad de minutos obtener la cantidad de dias que transcurren
  const getDaysFromMinutes = (minutes) => {
    if (!minutes) return 0;
    return Math.floor(minutes / 1440);
  };

  // combinar una fecha con una hora de moment
  const combineDateAndTime = (date, time) => {
    if (!date || !time) return null;
    time = moment(time);
    return date.set({
      hour: time.hour(),
      minute: time.minute(),
      second: time.second(),
      millisecond: time.millisecond(),
    });
  };

  const getEndOfTask = (index) => {
    if (index === undefined || index === null) return null;
    let horaFin = getValues(`${name}[${index}].horaFin`);
    let tiempoEstimado = getValues(`${name}[${index}].tiempoEstimado`);
    let fechaInicio = getFechaEjecucion(index);
    if (fechaInicio && tiempoEstimado) {
      fechaInicio.add(getDaysFromMinutes(tiempoEstimado), 'days');
    }
    // console.log('%c⧭ getEndOfTask [fechaInicio]', 'color: #00736b', fechaInicio?.format('YYYY-MM-DD HH:mm:ss'));
    // console.log('%c⧭ getEndOfTask [horaFin]', 'color: #00736b', moment(horaFin)?.format('YYYY-MM-DD HH:mm:ss'));
    return horaFin ? combineDateAndTime(fechaInicio, horaFin) : fechaInicio;
  };

  const getStartOfTask = (index) => {
    if (index === undefined || index === null) return null;
    let horaInicio = getHoraInicio(index);
    let fechaInicio = getFechaEjecucion(index);
    return horaInicio ? combineDateAndTime(fechaInicio, horaInicio) : fechaInicio;
  };

  const setFechaEjecucion = (index, item, newFechaEjecucion) => {
    setValue(`${name}[${index}].fechaEjecucion`, newFechaEjecucion.toDate());
    const inicioProgramacion = getValues(`${name}[${index}].inicioProgramacion`);
    if (getValues(`${name}[${index}].programar`) && inicioProgramacion) {
      setValue(`${name}[${index}].inicioProgramacion`, newFechaEjecucion.toDate());
      onChangeInicioProgramacion(index, newFechaEjecucion, item);
    }
  };

  const setHoraInicio = (index, item, newHoraInicio) => {
    const fechaMinimaValida = calcMinDate(item, newHoraInicio);
    const horaInicio = fechaMinimaValida;
    setValue(`${name}[${index}].horaInicio`, horaInicio?.toDate());
    const tiempoEstimado = getValues(`${name}[${index}].tiempoEstimado`);
    const newHoraFin = calcHoraFin(horaInicio?.toDate(), tiempoEstimado);
    setHoraFin(index, item, newHoraFin);
    return true;
  };

  const isPositiveOrZero = (value: any) => {
    return value !== null && value !== undefined && value >= 0;
  };

  const setHoraFin = (index, item, newHoraFin) => {
    const horaFin = newHoraFin;
    setValue(`${name}[${index}].horaFin`, horaFin?.toDate());
    setStartOfSuccessors(index, item, horaFin);
  };

  const setStartOfSuccessors = (index, item, newHoraFin: any = null) => {
    const endOfTask = newHoraFin || getEndOfTask(index);
    if (!endOfTask) return;
    const successors = item.tarea.successors || [];
    for (let s of successors) {
      const sIndex = fields.findIndex((t: any) => t.tarea.id === s);
      if (sIndex === -1) continue;
      const startOfSuccessor = getStartOfTask(sIndex);
      if (endOfTask.isAfter(startOfSuccessor)) {
        // actualizar el final de la tarea sucesora de forma recursiva
        setFechaEjecucion(sIndex, fields[sIndex], endOfTask);
        setHoraInicio(sIndex, fields[sIndex], endOfTask);
      }
    }
    return;
  };

  const onChangeFechaEjecucion = (index, value, item) => {
    const newHoraInicio = combineDateAndTime(moment(value), getHoraInicio(index));
    setHoraInicio(index, item, newHoraInicio);
  };

  const onChangeInicioProgramacion = (index, value, item) => {
    calcInicioProgramacion(index);
  };

  const onChangeDaysOfWeek = (index, value, item) => {
    calcInicioProgramacion(index);
  };

  const onChangeHoraInicio = (index, value, item) => {
    const newHoraInicio = combineDateAndTime(getFechaEjecucion(index), moment(value));
    setHoraInicio(index, item, newHoraInicio);
  };

  const onChangeTiempoEstimado = (index, value, item) => {
    const horaInicio = getHoraInicio(index);
    if (horaInicio) {
      const horaFin = calcHoraFin(horaInicio, value);
      setHoraFin(index, item, horaFin);
    }
  };

  const onChangeHoraFin = (index, value, item) => {
    // calcular fecha base considerando días de ejecución
    const tiempoEstimado = getTiempoEstimado(index);
    const fechaBase = getFechaEjecucion(index); // no es necesario calcular la fecha mínima porque se calcula antes de asignar la fecha de ejecución
    if (tiempoEstimado && fechaBase) {
      // obtener días desde los minutos y sumarlos a la fecha base de ejecución
      fechaBase.add(getDaysFromMinutes(tiempoEstimado), 'days');
    }
    const newHoraFin = combineDateAndTime(fechaBase, moment(value));
    const horaInicio = getHoraInicio(index);
    if (horaInicio) {
      const tiempoEstimado = calcTiempoEstimado(horaInicio, newHoraFin.toDate());
      setValue(`${name}[${index}].tiempoEstimado`, tiempoEstimado);
    }
    setHoraFin(index, item, newHoraFin);
  };

  //getTiempoEstimado
  const getTiempoEstimado = (index) => getValues(`${name}[${index}].tiempoEstimado`);

  const getFechaInicioProgramacionDefault = () => {
    const today = moment();
    const fechaEstimadaInicio = getValues(`planificacion.fechaEstimadaInicio`);
    if (!fechaEstimadaInicio) return today;
    const fechaInicioProgramacion = moment(fechaEstimadaInicio);
    if (fechaInicioProgramacion.isBefore(today)) {
      return today;
    }
    return fechaInicioProgramacion;
  };

  const getFechaFinProgramacionDefault = () => {
    const today = moment();
    const fechaEstimadaFin = getValues(`planificacion.fechaEstimadaFin`);
    if (!fechaEstimadaFin) return today;
    const fechaFinProgramacion = moment(fechaEstimadaFin);
    if (fechaFinProgramacion.isBefore(today)) {
      return today;
    }
    return fechaFinProgramacion;
  };

  // formato moment segun tiempo estimado
  const finFormat = (index) => {
    return 'DD/MM/YYYY [a las] HH:mm';
    /** probando otro formato
    const tiempoEstimado = getTiempoEstimado(index);
    if (tiempoEstimado && getDaysFromMinutes(tiempoEstimado) > 0) {
      return 'DD/MM/YYYY [a las] HH:mm';
    }
    return '[a las] HH:mm';*/
  };

  function RenderTitleDates(props) {
    const {index} = props;
    useWatch({name: `${name}[${index}].fechaEjecucion`});
    useWatch({name: `${name}[${index}].inicioProgramacion`});
    useWatch({name: `${name}[${index}].horaInicio`});
    useWatch({name: `${name}[${index}].horaFin`});
    const fechaStr = getFechaEjecucion(index)?.format('DD/MM/YYYY') || '_';
    const horaStr = asignarHoraInicio ? ` a las ${getStartOfTask(index)?.format('HH:mm') || '_'}` : '';
    const finStr = asignarHoraInicio ? getEndOfTask(index)?.format(finFormat(index)) || '_' : null;
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 'auto', mr: 1}}>
        <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
          {fechaStr}
          {horaStr}
        </Typography>
        {finStr && (
          <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
            Fin: {finStr}
          </Typography>
        )}
      </Box>
    );
  }

  // asignar hora inicio y fin de la tarea y sus sucesoras recursivamente
  const asignarHoraInicioFinTareasSucesoras = (index, item) => {
    const horaInicio = getHoraInicio(index);
    const tiempoEstimado = getTiempoEstimado(index);
    if (horaInicio && tiempoEstimado) {
      const horaFin = moment(horaInicio).add(tiempoEstimado, 'minutes');
      setHoraFin(index, item, horaFin);
      asignarInicioSucesoras(index, item, horaFin);
    }
  };

  // asignar inicio de las sucesoras sin importar si tienen hora de inicio
  const asignarInicioSucesoras = (index, item, newHoraInicio) => {
    const successors = item.tarea?.successors || [];
    for (let s of successors) {
      const sIndex = fields.findIndex((t: any) => t.tarea.id === s);
      if (sIndex === -1) continue;
      const sItem = fields[sIndex];
      setHoraInicio(sIndex, sItem, newHoraInicio);
      asignarHoraInicioFinTareasSucesoras(sIndex, sItem); // recursividad
    }
  };

  // recalcular horas de inicio y fin de las tareas sucesoras
  const recalcularHoraInicioFinTareasSucesoras = () => {
    fields.forEach((item: any, index) => {
      const predecessors = item.tarea.predecessors || [];
      if (predecessors.length === 0) {
        asignarHoraInicioFinTareasSucesoras(index, item);
      }
    });
  };

  /**
   * Copiar configuracion de 1 tarea programada (para poder pegarla en otras tareas)
   */
  const [configuracionCopiada, setConfiguracionCopiada] = useState<any>(null);
  const copiarConfiguracionTarea = (index) => {
    const item = getValues(`${name}[${index}]`);
    const copiada = {copyIndex: index, ...item};
    //console.log('%c⧭', 'color: #e57373', {copiada});
    setConfiguracionCopiada(copiada);
    Message.success('Configuración copiada');
  };

  /**
   * Pegar configuración tarea
   */
  const pegarConfiguracionTarea = (index) => {
    if (!configuracionCopiada) return;
    const copyIndex = configuracionCopiada.copyIndex;
    if (copyIndex === index) return;
    const copyItem = configuracionCopiada;
    const copyItemKeys = Object.keys(copyItem);
    for (let key of copyItemKeys) {
      if (
        [
          'copyIndex',
          'tarea',
          'id',
          '_id',
          // 'horaInicio',
          // 'horaFin',
          // 'tiempoEstimado',
          // 'inicioProgramacion',
          // 'fechaEjecucion',
          // 'fechaFinProgramacion',
          // 'fechaInicioProgramacion',
          // 'fechaFin',
          // 'fechaInicio',
          // 'programar',
        ].includes(key)
      )
        continue;
      setValue(`${name}[${index}].${key}`, copyItem[key]);
    }
    const pegada = getValues(`${name}[${index}]`);
    //console.log('%c⧭', 'color: #e57373', {pegada});
    Message.success('Configuración pegada');
  };

  // Tareas no expirables, no vencen, y se pueden asignar al pasado también
  const checkExpirable = (index) => {
    const tarea = getValues(`${name}[${index}]`);
    const isUnExpirable = !!tarea?.unexpirable;
    //console.log('%c⧭', 'color: #f200e2', {isUnExpirable});
    return !isUnExpirable;
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{p: 2, mb: 2}}>
        <Grid container direction="row" alignItems="center" spacing={2}>
          <Grid item>
            <SwitchFormItem
              defaultValue={asignarHoraInicio}
              onChange={(value) => {
                setValue('asignarHoraInicio', value);
                if (value) {
                  fields.forEach((item: any, index) => {
                    const horaInicio = combineDateAndTime(getFechaEjecucion(index), new Date());
                    setValue(`${name}[${index}].horaInicio`, horaInicio.toDate());
                    setValue(`${name}[${index}].tiempoEstimado`, tiempoEstimadoDefault);
                    setValue(`${name}[${index}].horaFin`, horaInicio.add(tiempoEstimadoDefault, 'minutes').toDate());
                  });
                }
                fields.forEach((item: any, index) => {
                  if (value) {
                    const predecessors = item.tarea.predecessors || [];
                    if (predecessors.length === 0) {
                      const horaInicio = combineDateAndTime(getFechaEjecucion(index), new Date());
                      setHoraInicio(index, item, horaInicio);
                    }
                  } else {
                    setValue(`${name}[${index}].horaInicio`, null);
                    setValue(`${name}[${index}].horaFin`, null);
                    setValue(`${name}[${index}].tiempoEstimado`, null);
                  }
                });
              }}
              name="asignarHoraInicio"
              label={i18n('entities.planificadorTarea.fields.asignarHoraInicio')}
            />
          </Grid>
          {asignarHoraInicio && (
            <>
              <Grid item>
                <InputNumberFormItem
                  style={{minWidth: 220}}
                  defaultValue={tiempoEstimadoDefault}
                  name="tiempoEstimadoDefault"
                  label={i18n('entities.planificadorTarea.fields.tiempoEstimadoDefault')}
                  disableFormat={true}
                />
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  size="medium"
                  onClick={() => {
                    fields.forEach((item: any, index) => {
                      setValue(`${name}[${index}].tiempoEstimado`, tiempoEstimadoDefault);
                    });
                    recalcularHoraInicioFinTareasSucesoras();
                  }}>
                  {i18n('common.apply')}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      {fields.map((item: any, index) => (
        <Accordion
          sx={{boxShadow: '0px 2px 4px rgba(0,0,0,0.08)'}}
          expanded={expanded === index}
          onChange={handleChangeAccordion(index)}
          disableGutters>
          <AccordionSummary
            sx={{
              minHeight: 56,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
            }}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header">
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%', pr: 1}}>
              <Typography variant="subtitle1" component="span" sx={{whiteSpace: 'nowrap', fontWeight: 600}}>
                {item.order || index + 1}.
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  ml: 1,
                }}>
                {!fromPlanificacion && (
                  <Typography variant="subtitle1" component="span">
                    <ModeloTareaListItem value={watch(`${name}[${index}].tarea`)} />
                  </Typography>
                )}
                {fromPlanificacion && (
                  <HtmlTooltip
                    disableHoverListener={false}
                    enterDelay={800}
                    leaveDelay={100}
                    enterNextDelay={300}
                    followCursor
                    title={
                      <React.Fragment>
                        <Typography color="inherit">Predecesoras:</Typography>
                        <ul style={{textAlign: 'left'}}>
                          {item.tarea?.predecessors?.map((predecessor: any) => {
                            const predecessorItem: any = fields.find((p: any) => p.tarea?.id === predecessor);
                            return (
                              <li key={predecessor} style={{marginLeft: -20, marginTop: -2}}>
                                <Typography color="inherit">
                                  {predecessorItem?.order} {predecessorItem?.tarea?.titulo}
                                </Typography>
                              </li>
                            );
                          })}
                        </ul>
                      </React.Fragment>
                    }>
                    <Typography variant="subtitle1" component="span">
                      {item.tarea?.titulo}
                    </Typography>
                  </HtmlTooltip>
                )}
              </Box>
              <RenderTitleDates index={index} />
              {!props.disabled && (
                <BasicItemMenu
                  openButton={(buttonProps) => (
                    <IconButton
                      aria-label="more"
                      aria-controls="long-menu"
                      aria-haspopup="true"
                      onClick={(event) => {
                        event.stopPropagation();
                        buttonProps.onClick();
                      }}
                      sx={{ml: 0.5}}
                      size="small">
                      <MoreVert />
                    </IconButton>
                  )}
                  options={[
                    {
                      label: i18n('entities.planificadorTarea.taskMenuOptions.copiarConfiguracion'),
                      onClick: () => copiarConfiguracionTarea(index),
                    },
                    {
                      label: i18n('entities.planificadorTarea.taskMenuOptions.pegarConfiguracion'),
                      onClick: () => setConfirmarPegarIndex(index),
                      hide: !configuracionCopiada || configuracionCopiada?.copyIndex === index,
                    },
                    {
                      label: i18n('entities.planificadorTarea.taskMenuOptions.quitarTarea'),
                      onClick: () => setConfirmarQuitarIndex(index),
                    },
                  ]}
                />
              )}
            </Box>
            {confirmarPegarIndex === index && (
              <ConfirmModal
                title={i18n('common.areYouSureYouWantToPasteTaskConfiguration')}
                onConfirm={(event) => {
                  event.stopPropagation();
                  pegarConfiguracionTarea(index);
                  setConfirmarPegarIndex(null);
                }}
                onClose={(event) => {
                  event.stopPropagation();
                  setConfirmarPegarIndex(null);
                }}
                okText={i18n('common.yes')}
                cancelText={i18n('common.no')}
              />
            )}
            {confirmarQuitarIndex === index && (
              <ConfirmModal
                title={i18n('common.areYouSureYouWantToRemoveThisTaskFromTheSchedule')}
                onConfirm={(event) => {
                  event.stopPropagation();
                  remove(confirmarQuitarIndex);
                  Message.success(i18n('common.taskRemovedFromSchedule'));
                  setConfiguracionCopiada(null);
                  setConfirmarQuitarIndex(null);
                }}
                onClose={(event) => {
                  event.stopPropagation();
                  setConfirmarQuitarIndex(null);
                }}
                okText={i18n('common.yes')}
                cancelText={i18n('common.no')}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                px: 3,
                width: '100%',
                ...(disabled && {
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }),
              }}
              key={item.id}>
              {/* Sección: Identidad */}
              <Box sx={{mt: -1}}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
                    Identidad
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
                    Opcionalmente asignar un subtitulo, folio y/o nivel de prioridad
                  </Typography>
                </Box>
                <Table size="small">
                  <TableBody>
                    {!fromPlanificacion && (
                      <TableRow sx={{'&:last-child td': {border: 0}}}>
                        <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                          <Typography variant="body2" color="text.secondary">
                            {i18n('entities.planificadorTarea.fields.tareas')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{py: 1}}>
                          <ModeloTareaAutocompleteFormItem
                            name={`${name}[${index}].tarea`}
                            disabled={disabled || fromPlanificacion}
                            entity="modelo-tarea"
                            filter={{
                              planificaciones: watch('planificacion')?.id,
                            }}
                            onChange={(value) => {
                              const selectedRecord = value?.record || null;

                              if (selectedRecord) {
                                const tareaAnterior = getValues(`${name}[${index}].tarea`) || item.tarea;

                                const nuevaTarea = {
                                  ...selectedRecord,
                                  successors: tareaAnterior?.successors || [],
                                  predecessors: tareaAnterior?.predecessors || [],
                                  order: tareaAnterior?.order || index + 1,
                                  type: tareaAnterior?.type || 0,
                                };

                                setValue(`${name}[${index}].tarea`, nuevaTarea, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });

                                setValue(`${name}[${index}].customSequenceId`, resolveCustomSequenceValue(
                                  settings?.customSequences || [],
                                  selectedRecord.customSequenceId,
                                  selectedRecord.customSequencePrefix,
                                ), {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });

                                setValue(`${name}[${index}].prioridad`, selectedRecord.prioridad || null, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });
                              } else {
                                setValue(`${name}[${index}].tarea`, null, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });

                                setValue(`${name}[${index}].customSequenceId`, null, {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                            label=""
                            required={true}
                            showCreate={true}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow sx={{'&:last-child td': {border: 0}}}>
                      <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                        <Typography variant="overline" sx={{fontWeight: 400, display: 'block', mb: 0.5}}>
                          TITULO
                        </Typography>
                      </TableCell>
                      <TableCell sx={{py: 1}}>
                        <Typography variant="overline" sx={{fontWeight: 600, display: 'block', mb: 0.5}}>
                          {item.tarea?.titulo}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{'&:last-child td': {border: 0}}}>
                      <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                        <Typography variant="body2" color="text.secondary">
                          {i18n('entities.tarea.fields.subtitulo')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{py: 1}}>
                        <InputFormItem name={`${name}[${index}].subtitulo`} disabled={disabled} label="" required={false} />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{'&:last-child td': {border: 0}}}>
                      <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                        <Typography variant="body2" color="text.secondary">
                          Folio
                        </Typography>
                      </TableCell>
                      <TableCell sx={{py: 1}}>
                        <SelectFormItem
                          name={`${name}[${index}].customSequenceId`}
                          label=""
                          options={sequenceOptions}
                          required={false}
                          disabled={disabled}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{'&:last-child td': {border: 0}}}>
                      <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                        <Typography variant="body2" color="text.secondary">
                          {i18n('entities.modeloTarea.fields.prioridad')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{py: 1}}>
                        <SelectFormItem
                          name={`${name}[${index}].prioridad`}
                          disabled={props.disabled}
                          label=""
                          options={modeloTareaEnumerators.prioridad.map((prioridadItem) => ({
                            value: prioridadItem,
                            label: i18n(`entities.modeloTarea.enumerators.prioridad.${prioridadItem}`),
                          }))}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {watch(`${name}[${index}].tarea`)?.id && (
                <>
                  {/* Sección: Asignación */}
                  <Box sx={{mt: 2}}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
                        Asignación
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
                        A quién se asignará la tarea y quiénes deben aceptarla o validarla
                      </Typography>
                    </Box>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{'&:last-child td': {border: 0}}}>
                          <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                            <Typography variant="body2" color="text.secondary">
                              {i18n('entities.planificadorTarea.fields.dirigidoA')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{py: 1}}>
                            <SelectFormItem
                              name={`${name}[${index}].dirigidoA`}
                              disabled={disabled}
                              label=""
                              options={planificadorTareaEnumerators.dirigidoA.map((value) => ({
                                value,
                                label: i18n(`entities.planificadorTarea.enumerators.dirigidoA.${value}`),
                              }))}
                              required={false}
                            />
                          </TableCell>
                        </TableRow>
                        {watch(`${name}[${index}].dirigidoA`) === 'Teams' && (
                          <TableRow sx={{'&:last-child td': {border: 0}}}>
                            <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                              <Typography variant="body2" color="text.secondary">
                                Teams
                              </Typography>
                            </TableCell>
                            <TableCell sx={{py: 1}}>
                              <AsignadorFuncionAutocompleteFormItem
                                entity="asignadorFuncion"
                                name={`${name}[${index}].teams`}
                                disabled={disabled}
                                label=""
                                required={false}
                                showCreate={true}
                                mode="multiple"
                                menuPortalTarget={document.body}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        {watch(`${name}[${index}].dirigidoA`) === 'Ejecutor' && (
                          <TableRow sx={{'&:last-child td': {border: 0}}}>
                            <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                              <Typography variant="body2" color="text.secondary">
                                {i18n('entities.planificadorTarea.fields.ejecutores')}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{py: 1}}>
                              <UserAutocompleteFormItem
                                entity="user"
                                name={`${name}[${index}].ejecutores`}
                                disabled={disabled}
                                label=""
                                required={false}
                                showCreate={true}
                                mode="multiple"
                                menuPortalTarget={document.body}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        {watch(`${name}[${index}].dirigidoA`) === 'Puesto' && (
                          <TableRow sx={{'&:last-child td': {border: 0}}}>
                            <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                              <Typography variant="body2" color="text.secondary">
                                {i18n('entities.planificadorTarea.fields.puestos')}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{py: 1}}>
                              <PuestoAutocompleteFormItem
                                entity="puesto"
                                name={`${name}[${index}].puestos`}
                                disabled={disabled}
                                label=""
                                required={false}
                                showCreate={true}
                                mode="multiple"
                                menuPortalTarget={document.body}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{'&:last-child td': {border: 0}}}>
                          <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                            <Typography variant="body2" color="text.secondary">
                              {i18n('entities.planificadorTarea.fields.esVisada')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{py: 1}}>
                            <SwitchFormItem name={`${name}[${index}].esVisada`} disabled={disabled} label="" />
                          </TableCell>
                        </TableRow>
                        {watch(`${name}[${index}].esVisada`) && (
                          <>
                            <TableRow sx={{'&:last-child td': {border: 0}}}>
                              <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                <Typography variant="body2" color="text.secondary">
                                  {i18n('entities.planificadorTarea.fields.aceptadores')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{py: 1}}>
                                <UserAutocompleteFormItem
                                  name={`${name}[${index}].aceptadores`}
                                  disabled={disabled}
                                  entity="user"
                                  label=""
                                  required={false}
                                  showCreate={true}
                                  mode="multiple"
                                  menuPortalTarget={document.body}
                                />
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{'&:last-child td': {border: 0}}}>
                              <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                <Typography variant="body2" color="text.secondary">
                                  {i18n('entities.planificadorTarea.fields.validadores')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{py: 1}}>
                                <UserAutocompleteFormItem
                                  entity="user"
                                  name={`${name}[${index}].validadores`}
                                  disabled={disabled}
                                  label=""
                                  required={false}
                                  showCreate={true}
                                  mode="multiple"
                                  menuPortalTarget={document.body}
                                  validatorAssignable
                                />
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </Box>

                  {watch(`${name}[${index}].dirigidoA`) &&
                    (watch(`${name}[${index}].teams`)?.length > 0 ||
                      watch(`${name}[${index}].ejecutores`)?.length > 0 ||
                      watch(`${name}[${index}].puestos`)?.length > 0) && (
                      <>
                        {/* Sección: Cuándo (Programación) */}
                        <Box sx={{mt: 2}}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
                              Cuándo
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
                              Fecha única o programación recurrente con periodicidad
                            </Typography>
                          </Box>
                          <Table size="small">
                            <TableBody>
                              <TableRow sx={{'&:last-child td': {border: 0}}}>
                                <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                  <Typography variant="body2" color="text.secondary">
                                    {i18n('entities.planificadorTarea.fields.programar')}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{py: 1}}>
                                  <SwitchFormItem
                                    name={`${name}[${index}].programar`}
                                    onChange={(value) => {
                                      if (value) {
                                        setValue(
                                          `${name}[${index}].inicioProgramacion`,
                                          getFechaInicioProgramacionDefault().toDate(),
                                        );
                                        setValue(`${name}[${index}].finProgramacion`, getFechaFinProgramacionDefault().toDate());
                                        setValue(`${name}[${index}].tipoPeriodicidad`, 'days');
                                        setValue(`${name}[${index}].periodicidad`, 1);
                                      } else {
                                        setValue(`${name}[${index}].inicioProgramacion`, null);
                                        setValue(`${name}[${index}].finProgramacion`, null);
                                        setValue(`${name}[${index}].tipoPeriodicidad`, null);
                                        setValue(`${name}[${index}].periodicidad`, null);
                                        setValue(`${name}[${index}].daysOfWeek`, []);
                                      }
                                    }}
                                    disabled={disabled}
                                    label=""
                                  />
                                </TableCell>
                              </TableRow>
                              {!watch(`${name}[${index}].programar`) && (
                                <TableRow sx={{'&:last-child td': {border: 0}}}>
                                  <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                    <Typography variant="body2" color="text.secondary">
                                      {i18n('entities.planificadorTarea.fields.fechaEjecucion')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{py: 1}}>
                                    <DatePickerFormItem
                                      minDate={
                                        checkExpirable(index)
                                          ? calcMinDate(item, getFechaInicioProgramacionDefault()).toISOString()
                                          : undefined
                                      }
                                      maxDate={moment(watch('planificacion')?.fechaEstimadaFin).toISOString()}
                                      disabled={disabled}
                                      name={`${name}[${index}].fechaEjecucion`}
                                      label=""
                                      required={false}
                                      onChange={(value) => onChangeFechaEjecucion(index, value, item)}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                              {watch(`${name}[${index}].programar`) && (
                                <>
                                  <TableRow sx={{'&:last-child td': {border: 0}}}>
                                    <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                      <Typography variant="body2" color="text.secondary">
                                        {i18n('entities.planificadorTarea.fields.inicioProgramacion')}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{py: 1}}>
                                      <DatePickerFormItem
                                        minDate={
                                          checkExpirable(index)
                                            ? calcMinDate(item, getFechaInicioProgramacionDefault()).toISOString()
                                            : undefined
                                        }
                                        maxDate={getFechaFinProgramacionDefault().toISOString()}
                                        disabled={disabled}
                                        name={`${name}[${index}].inicioProgramacion`}
                                        label=""
                                        required={false}
                                        onChange={(value) => onChangeInicioProgramacion(index, value, item)}
                                      />
                                    </TableCell>
                                  </TableRow>
                                  <TableRow sx={{'&:last-child td': {border: 0}}}>
                                    <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                      <Typography variant="body2" color="text.secondary">
                                        {i18n('entities.planificadorTarea.fields.finProgramacion')}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{py: 1}}>
                                      <DatePickerFormItem
                                        minDate={
                                          getStartOfTask(index)?.toISOString() ||
                                          moment(watch(`${name}[${index}].inicioProgramacion`)).toISOString()
                                        }
                                        maxDate={getFechaFinProgramacionDefault().toISOString()}
                                        name={`${name}[${index}].finProgramacion`}
                                        disabled={disabled}
                                        label=""
                                        required={false}
                                      />
                                    </TableCell>
                                  </TableRow>
                                </>
                              )}
                              <TableRow sx={{'&:last-child td': {border: 0}}}>
                                <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                  <Typography variant="body2" color="text.secondary">
                                    Cantidad por fecha
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{py: 1}}>
                                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Box sx={{maxWidth: 100}}>
                                      <InputNumberFormItem
                                        name={`${name}[${index}].cantidadPorEjecucion`}
                                        disabled={disabled}
                                        label=""
                                        required={true}
                                        defaultValue={1}
                                        disableFormat={true}
                                        onChange={(val) => {
                                          if (!val || Number(val) < 1) setValue(`${name}[${index}].cantidadPorEjecucion`, 1);
                                        }}
                                      />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      tareas idénticas por fecha
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {watch(`${name}[${index}].programar`) &&
                                watch(`${name}[${index}].inicioProgramacion`) &&
                                watch(`${name}[${index}].finProgramacion`) && (
                                  <>
                                    <TableRow sx={{'&:last-child td': {border: 0}}}>
                                      <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                        <Typography variant="body2" color="text.secondary">
                                          {i18n('entities.planificadorTarea.fields.tipoPeriodicidad')}
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{py: 1}}>
                                        <RadioFormItem
                                          name={`${name}[${index}].tipoPeriodicidad`}
                                          disabled={disabled}
                                          label=""
                                          options={planificadorTareaEnumerators.tipoPeriodicidad.map((value) => ({
                                            value,
                                            label: i18n(`entities.planificadorTarea.enumerators.tipoPeriodicidad.${value}`),
                                          }))}
                                          required={false}
                                        />
                                      </TableCell>
                                    </TableRow>
                                    <TableRow sx={{'&:last-child td': {border: 0}}}>
                                      <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                        <Typography variant="body2" color="text.secondary">
                                          {i18n('entities.planificadorTarea.fields.periodicidad')}
                                        </Typography>
                                      </TableCell>
                                      <TableCell sx={{py: 1}}>
                                        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                          <Typography variant="body2">Repetir cada</Typography>
                                          <Box sx={{maxWidth: 80}}>
                                            <InputNumberFormItem
                                              name={`${name}[${index}].periodicidad`}
                                              disabled={disabled}
                                              label=""
                                              required={true}
                                            />
                                          </Box>
                                          {watch(`${name}[${index}].tipoPeriodicidad`) && (
                                            <Typography variant="body2">
                                              {i18n(
                                                `entities.planificadorTarea.enumerators.tipoPeriodicidad.${watch(
                                                  `${name}[${index}].tipoPeriodicidad`,
                                                )}`,
                                              ).toLowerCase()}
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                    {watch(`${name}[${index}].tipoPeriodicidad`) === 'weeks' && (
                                      <TableRow sx={{'&:last-child td': {border: 0}}}>
                                        <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                          <Typography variant="body2" color="text.secondary">
                                            Días de la semana
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{py: 1}}>
                                          <DayOfWeekPicker
                                            name={`${name}[${index}].daysOfWeek`}
                                            onChange={(value) => onChangeDaysOfWeek(index, value, item)}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </>
                                )}
                            </TableBody>
                          </Table>
                          {watch(`${name}[${index}].programar`) &&
                            (() => {
                              const formItem = getValues(`${name}[${index}]`);
                              const currentItem = {
                                ...item,
                                ...formItem,
                                tarea: formItem?.tarea ?? item?.tarea,
                              };
                              const result = calcTareasCount(currentItem);
                              if (result?.canCalculate) {
                                return (
                                  <Box
                                    sx={{
                                      mt: 2,
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1.5,
                                      px: 2,
                                      py: 1.5,
                                      borderRadius: 1,
                                      bgcolor: (theme) =>
                                        theme.palette.mode === 'light' ? '#e8f4fd' : 'rgba(25, 118, 210, 0.16)',
                                      borderLeft: '4px solid',
                                      borderColor: 'info.main',
                                    }}>
                                    <InfoOutlined
                                      sx={{
                                        color: 'info.main',
                                        fontSize: 22,
                                        mt: 0.2,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography variant="body2" sx={{color: 'text.primary', lineHeight: 1.6}}>
                                      Esta configuración generará <strong>{result.count} tareas por ejecutor</strong>
                                      {result.fechasCount > 1 && (
                                        <>
                                          {' '}
                                          ({result.cantidadPorEjecucion} por fecha × {result.fechasCount} fechas)
                                        </>
                                      )}
                                      .
                                    </Typography>
                                  </Box>
                                );
                              }
                              return null;
                            })()}
                        </Box>

                        {/* Sección: Plazos */}
                        <Box sx={{mt: 2}}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
                              Plazos
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
                              Límites de tiempo para completar y reglas de vencimiento
                            </Typography>
                          </Box>
                          <Table size="small">
                            <TableBody>
                              <TableRow sx={{'&:last-child td': {border: 0}}}>
                                <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                  <Typography variant="body2" color="text.secondary">
                                    {i18n('entities.planificadorTarea.fields.tipoTiempoLimite')}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{py: 1}}>
                                  <RadioFormItem
                                    name={`${name}[${index}].tipoTiempoLimite`}
                                    disabled={disabled}
                                    label=""
                                    options={planificadorTareaEnumerators.tipoTiempoLimite.map((value) => ({
                                      value,
                                      label: i18n(`entities.planificadorTarea.enumerators.tipoTiempoLimite.${value}`),
                                    }))}
                                    required={false}
                                  />
                                </TableCell>
                              </TableRow>
                              {watch(`${name}[${index}].tipoTiempoLimite`) && (
                                <TableRow sx={{'&:last-child td': {border: 0}}}>
                                  <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                    <Typography variant="body2" color="text.secondary">
                                      {i18n('entities.planificadorTarea.fields.tiempoLimite')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{py: 1}}>
                                    <InputNumberFormItem
                                      name={`${name}[${index}].tiempoLimite`}
                                      disabled={disabled}
                                      onChange={(value) => {
                                        if (!isPositiveOrZero(value)) value = 0;
                                        setValue(`${name}[${index}].tiempoLimite`, value);
                                      }}
                                      label=""
                                      required={true}
                                      disableFormat={true}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                              <TableRow sx={{'&:last-child td': {border: 0}}}>
                                <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                  <Typography variant="body2" color="text.secondary">
                                    {i18n('entities.planificadorTarea.fields.tiempoLimiteDiasHabiles')}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{py: 1}}>
                                  <SwitchFormItem
                                    name={`${name}[${index}].tiempoLimiteDiasHabiles`}
                                    disabled={disabled}
                                    label=""
                                  />
                                </TableCell>
                              </TableRow>
                              <TableRow sx={{'&:last-child td': {border: 0}}}>
                                <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                  <Tooltip
                                    disableHoverListener={false}
                                    enterDelay={800}
                                    leaveDelay={100}
                                    enterNextDelay={300}
                                    followCursor
                                    title={i18n('common.theyWontExpireWhenTimeLimitIsReached')}>
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      {i18n('entities.planificadorTarea.fields.unexpirable')}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{py: 1}}>
                                  <SwitchFormItem name={`${name}[${index}].unexpirable`} disabled={disabled} label="" />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>

                        {asignarHoraInicio && (
                          <Box sx={{mt: 2}}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography variant="overline" sx={{fontWeight: 600, display: 'block'}}>
                                Horarios
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{textTransform: 'none', display: 'block'}}>
                                Hora de inicio, duración estimada y hora de fin
                              </Typography>
                            </Box>
                            <Table size="small">
                              <TableBody>
                                <TableRow sx={{'&:last-child td': {border: 0}}}>
                                  <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                    <Typography variant="body2" color="text.secondary">
                                      {i18n('entities.planificadorTarea.fields.horaInicio')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{py: 1}}>
                                    <DatePickerFormItem
                                      timeOnly
                                      minDate={() => calcMinDate(item)}
                                      defaultValue={getStartOfTask(index)}
                                      name={`${name}[${index}].horaInicio`}
                                      disabled={disabled}
                                      label=""
                                      required={false}
                                      onChange={(value) => onChangeHoraInicio(index, value, item)}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow sx={{'&:last-child td': {border: 0}}}>
                                  <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                    <Typography variant="body2" color="text.secondary">
                                      {i18n('entities.planificadorTarea.fields.tiempoEstimado')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{py: 1}}>
                                    <InputNumberFormItem
                                      disableFormat
                                      name={`${name}[${index}].tiempoEstimado`}
                                      disabled={disabled}
                                      label=""
                                      required={false}
                                      onBlur={() => {
                                        const value = getValues(`${name}[${index}].tiempoEstimado`);
                                        onChangeTiempoEstimado(index, value, item);
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow sx={{'&:last-child td': {border: 0}}}>
                                  <TableCell sx={{width: 180, py: 1}} component="th" scope="row">
                                    <Typography variant="body2" color="text.secondary">
                                      {i18n('entities.planificadorTarea.fields.horaFin')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{py: 1}}>
                                    <DatePickerFormItem
                                      timeOnly
                                      minDate={() => getStartOfTask(index)}
                                      defaultValue={getEndOfTask(index)}
                                      name={`${name}[${index}].horaFin`}
                                      disabled={disabled}
                                      label=""
                                      required={false}
                                      onChange={(value) => onChangeHoraFin(index, value, item)}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow sx={{'&:last-child td': {border: 0}}}>
                                  <TableCell colSpan={2} sx={{py: 1}}>
                                    <Tooltip title="Reasignar hora de inicio y fin a las tareas sucesoras">
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Redo />}
                                        onClick={() => asignarHoraInicioFinTareasSucesoras(index, item)}>
                                        Reasignar sucesoras
                                      </Button>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Box>
                        )}
                      </>
                    )}
                </>
              )}

              {/* {!fromPlanificacion && !props.disabled && (
                  <Grid item style={{marginTop: 40}} lg={12}>
                    <Button
                      size={'small'}
                      variant="contained"
                      color={'secondary'}
                      type="button"
                      onClick={() => doConfirm()}
                      style={{backgroundColor: 'red'}}>
                      <CloseIcon /> Eliminar Programación
                    </Button>
                  </Grid>
                )} */}

              {confirm && (
                <ConfirmModal
                  title={i18n('common.areYouSure')}
                  onConfirm={() => {
                    remove(index);
                    setConfirm(false);
                  }}
                  onClose={() => doCloseConfirm()}
                  okText={i18n('common.yes')}
                  cancelText={i18n('common.no')}
                />
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {!fromPlanificacion && !props.disabled && (
        <Box sx={{mt: 2}}>
          <Button variant="contained" color="primary" startIcon={<Add />} type="button" onClick={() => append({})}>
            Añadir Programación
          </Button>
        </Box>
      )}

      {showResumen && <PlanificacionResumenPanel onClose={props.onCloseResumen} />}
    </Box>
  );
}
